import React, { useEffect, useState } from "react";
import { Bell, X, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { usePushNotifications } from "../hooks/usePushNotifications";

const DISMISSED_AT_KEY = "infocity_notif_onboarding_dismissed_at";
const SNOOZE_DAYS = 7;
const SHOW_DELAY_MS = 3000;

// Global "Activá las notificaciones" prompt — mounted once for the whole app
// (not just inside Mensajes), so a candidate or employer who never opens
// Mensajes still gets a chance to opt in. Notification.requestPermission()
// only ever runs from the explicit "Activar notificaciones" click below,
// never automatically on load (required for iOS/PWA — a bare permission
// popup with no context gets auto-denied by users and can't be re-asked).
export function NotificationOnboardingPrompt() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { isSupported, permission, subscribe, subscribing } = usePushNotifications();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user || !isSupported || permission !== "default") { setVisible(false); return; }
    const dismissedAt = Number(localStorage.getItem(DISMISSED_AT_KEY) || 0);
    const daysSince = (Date.now() - dismissedAt) / 86400000;
    if (dismissedAt && daysSince < SNOOZE_DAYS) return;
    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [user, isSupported, permission]);

  if (!visible) return null;

  const dismiss = () => { localStorage.setItem(DISMISSED_AT_KEY, String(Date.now())); setVisible(false); };
  const activate = async () => { await subscribe(); setVisible(false); };

  return (
    <div data-testid="notification-onboarding-prompt"
      className="fixed bottom-20 sm:bottom-6 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-sm z-[65] p-4 bg-white border border-slate-200 rounded-2xl shadow-[0_10px_36px_rgba(15,23,42,0.16)]"
      style={{ marginBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-xl bg-[#028CF8]/10 flex items-center justify-center flex-shrink-0">
          <Bell className="h-4.5 w-4.5 text-[#028CF8]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-slate-900">{t("notifOnboarding.title")}</p>
          <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{t("notifOnboarding.body")}</p>
          <div className="flex items-center gap-2 mt-3">
            <button
              data-testid="notification-onboarding-activate-button"
              onClick={activate}
              disabled={subscribing}
              className="h-9 px-3.5 rounded-lg bg-[#028CF8] text-white text-xs font-semibold disabled:opacity-50 flex items-center gap-1.5"
            >
              {subscribing && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {t("notifOnboarding.activate")}
            </button>
            <button
              data-testid="notification-onboarding-dismiss-button"
              onClick={dismiss}
              className="h-9 px-3.5 rounded-lg text-xs font-medium text-slate-500 hover:bg-slate-50 transition-colors"
            >
              {t("notifOnboarding.notNow")}
            </button>
          </div>
        </div>
        <button onClick={dismiss} aria-label="close" data-testid="notification-onboarding-close" className="text-slate-400 hover:text-slate-600 flex-shrink-0">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
